import { ROLES } from '../../constants';
import {
  getAllOrganisationDetails,
  getGroupbyId,
  getPractiseAdminOrganisationDetailsById,
  getPractiseCode,
  getRealmRoles,
  getRealmUserInfo
} from '../../services';

interface FetchRolesArgs {
  userAccessToken: string;
  userRole: string;
}

const fetchRoles = async ({ userAccessToken, userRole }: FetchRolesArgs) => {
  if (!userAccessToken || !userRole) {
    throw new Error('Missing required parameters');
  }

  const roles = await getRealmRoles(userAccessToken);

  // Allowed roles
  const allowedRoles = [ROLES.PRACTISE_ADMIN, ROLES.DOCTOR, ROLES.DEPT_ADMIN, ROLES.OTHER];

  const filtered = roles?.length
    ? roles?.filter((role: { name: string }) => allowedRoles.includes(role.name))
    : [];

  // Hide PRACTISE_ADMIN if current user is PRACTISE_ADMIN
  const visibleRoles =
    userRole === ROLES.PRACTISE_ADMIN
      ? filtered?.filter((role: { name: string }) => role.name !== ROLES.PRACTISE_ADMIN)
      : filtered;

  // Map + sort
  const mappedRoles = visibleRoles
    .map((role: { name: string }) => ({
      label: role.name,
      value: role.name
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return [{ label: 'All', value: 'All' }, ...mappedRoles];
};

const fetchOrganisationViewMore = async (arg?: {
  organisationId: string;
  userAccessToken: string;
  role: string;
  generateSignedUrl: (arg: string) => Promise<string>;
}) => {
  if (!arg) throw new Error('Missing required parameters');

  const { organisationId, userAccessToken, generateSignedUrl, role } = arg;

  const result = await getAllOrganisationDetails(organisationId, role);
  const organisation = result.practiseOrganisations?.[0];
  const relations = organisation?.organisationPractiseProfileRelation || [];

  const firstRelation = relations[0];
  const deptId = firstRelation?.departmentId;

  if (!organisation?.id) {
    console.error('❌ organisation.id is missing');
  }

  if (!deptId) {
    console.error('❌ departmentId is missing in firstRelation:', firstRelation);
  }

  if (!firstRelation?.practiseUserId) {
    console.error('❌ practiseUserId is missing');
  }

  const [orgData] = await Promise.all([
    organisation?.id ? getGroupbyId(organisation.id, userAccessToken) : null,
    deptId ? getGroupbyId(deptId, userAccessToken) : null,
    firstRelation?.practiseUserId
      ? getRealmUserInfo(firstRelation.practiseUserId, userAccessToken)
      : null
  ]);

  const data = await Promise.all(
    relations.map(
      async (item: {
        departmentId: string;
        practiseUserId: string;
        practiseUserRelation: { practiseAdminLivePic: { imageContent: string } };
        role: string;
      }) => {
        const [dept, user] = await Promise.all([
          item.departmentId ? getGroupbyId(item.departmentId, userAccessToken) : null,
          item.practiseUserId ? getRealmUserInfo(item.practiseUserId, userAccessToken) : null
        ]);

        const imagePath = item.practiseUserRelation?.practiseAdminLivePic?.imageContent;
        const image = imagePath ? await generateSignedUrl(imagePath) : undefined;

        return {
          organisationId: organisation?.id,
          role: item.role,
          practiseUserId: item.practiseUserId,
          hospital: orgData?.name ?? 'N/A',
          department: dept?.name ?? 'N/A',
          image,
          doctorName: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
        };
      }
    )
  );

  return data;
};

export interface FetchOrganisationDataArgs {
  practiseAdminId: string;
  organisationId: string;
}

const fetchOrganisationData = async (arg: FetchOrganisationDataArgs) => {
  if (!arg?.practiseAdminId || !arg?.organisationId) {
    throw new Error('Missing parameters');
  }

  const { practiseAdminId, organisationId } = arg;

  // Fetch organisation details
  const data = await getPractiseAdminOrganisationDetailsById({
    practiseUserId: practiseAdminId,
    organisationId
  });

  const org = data?.practiseOrganisations?.[0];

  // Fetch practise code
  const practiceCode = await getPractiseCode({ practiseUserId: practiseAdminId });

  return {
    organisationId: org?.id,
    latitude: org?.latitude,
    longitude: org?.longitude,
    landmark: org?.landmark,
    address: org?.address,
    contact: org?.contact,
    alternateContact: org?.alternateContact,
    openingTime: org?.openingTime,
    closingTime: org?.closingTime,
    startDay: org?.startDay,
    endDay: org?.endDay,
    practiceCode: practiceCode?.userPractiseMapping?.[0]?.practiceCode as string,
    registrationNumber: org?.registrationNumber,
    websiteUrl: org?.websiteUrl,
    establishmentYear: org?.establishmentYear,
    logoUrl: org?.logoUrl,
    qrCode: data?.userPractiseMapping?.[0]?.qrSecret,
    role: data?.userPractiseMapping?.[0]?.role
  };
};

export { fetchRoles, fetchOrganisationViewMore, fetchOrganisationData };
